import ReactGA from 'react-ga4';
import { hasAnalyticsConsent, isAdminPath, isAnalyticsEnabledByEnv } from './analyticsConsent';
import { sendGoogleAdsPurchaseConversion } from './analyticsInit';
import { safeStorageGet, safeStorageSet } from './safeStorage';
import {
  buildPurchaseEventId,
  trackMetaCustomEvent,
  trackMetaEvent,
  trackMetaFromGa4,
  toMetaPayloadFromGa4Items
} from './metaPixel';

const CURRENCY = 'EUR';
const PURCHASE_SENT_PREFIX = 'ga4_purchase_sent:';
const sentPurchaseIds = new Set();

const isBrowser = () => typeof window !== 'undefined';

const isAnalyticsAllowed = () => {
  if (!isBrowser()) {
    return false;
  }

  if (!isAnalyticsEnabledByEnv() || !hasAnalyticsConsent()) {
    return false;
  }

  return !isAdminPath();
};

const parseLocalizedText = (value, fallback = 'Product') => {
  if (!value) {
    return fallback;
  }

  if (typeof value === 'object') {
    return value.EN || value.en || Object.values(value)[0] || fallback;
  }

  if (typeof value !== 'string') {
    return String(value);
  }

  try {
    const trimmed = value.trim();
    if (trimmed.startsWith('{')) {
      const parsed = JSON.parse(trimmed);
      return parsed.EN || parsed.en || Object.values(parsed)[0] || fallback;
    }
  } catch (error) {
    // Fall through to the raw value.
  }

  return value;
};

const toNumber = (value, fallback = 0) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
};

const getItemPrice = (item = {}) => {
  const source = item.Product || item;

  return toNumber(
    item.final_price ??
      item.price ??
      item.product_price ??
      source.final_price ??
      source.price ??
      source.product_price,
    0
  );
};

const getItemId = (item = {}) => {
  const source = item.Product || item;
  return String(item.item_id ?? item.id ?? item.product_id ?? source.product_id ?? source.id ?? '');
};

const getItemName = (item = {}) => {
  const source = item.Product || item;
  return parseLocalizedText(
    item.item_name ??
      item.name ??
      item.product_name ??
      item.product_title ??
      source.product_name ??
      source.product_title ??
      source.displayTitle,
    'Product'
  );
};

const getItemVariant = (item = {}) => {
  return (
    item.item_variant ||
    item.variant ||
    item.selectedVariationName ||
    item.variationName ||
    item.variation_name ||
    undefined
  );
};

const toGA4Item = (item = {}, index) => {
  const quantity = toNumber(item.quantity ?? item.cartItemQuantity, 1);
  const ga4Item = {
    item_id: getItemId(item),
    item_name: getItemName(item),
    price: getItemPrice(item),
    quantity
  };

  const category = item.item_category || item.category || item.categoryName || item.product_category;
  const variant = getItemVariant(item);

  if (category) {
    ga4Item.item_category = parseLocalizedText(category, String(category));
  }

  if (variant) {
    ga4Item.item_variant = parseLocalizedText(variant, String(variant));
  }

  if (Number.isInteger(index)) {
    ga4Item.index = index;
  }

  return ga4Item;
};

const toGA4Items = (items = []) => {
  if (!Array.isArray(items)) {
    return [];
  }

  return items
    .map((item, index) => toGA4Item(item, index))
    .filter((item) => item.item_id !== '');
};

const calculateGA4ItemsValue = (items = []) => {
  return items.reduce((sum, item) => {
    return sum + toNumber(item.price, 0) * toNumber(item.quantity, 1);
  }, 0);
};

const resolveEventValue = (value, items) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : calculateGA4ItemsValue(items);
};

const getListParams = ({ listId, listName } = {}) => ({
  item_list_id: String(listId || 'product_list'),
  item_list_name: String(listName || listId || 'Product List')
});

const sendGA4Event = (eventName, params) => {
  if (!isAnalyticsAllowed()) {
    return false;
  }

  if (process.env.NODE_ENV !== 'production') {
    console.debug('[GA4]', eventName, params);
  }

  if (!ReactGA?.isInitialized || typeof ReactGA.gtag !== 'function') {
    console.warn(`GA4 event "${eventName}" dropped: analytics not initialized`);
    return false;
  }

  ReactGA.gtag('event', eventName, params);

  return true;
};

const safelyTrack = (label, callback) => {
  try {
    return callback();
  } catch (error) {
    console.error(`marketingAnalytics ${label} failed:`, error);
    return false;
  }
};

const trackViewItemUnsafe = (product) => {
  const item = toGA4Item(product);
  const value = toNumber(item.price, 0);
  const items = [item];

  const ga4Sent = sendGA4Event('view_item', {
    currency: CURRENCY,
    value,
    items
  });
  // Meta is marketing-gated inside trackMetaFromGa4; independent of analytics consent.
  trackMetaFromGa4('view_item', { items, value });
  return ga4Sent;
};

export const trackViewItem = (product) => safelyTrack('trackViewItem', () => trackViewItemUnsafe(product));

const trackAddToCartUnsafe = (product, quantity = 1) => {
  const item = toGA4Item({ ...product, quantity });
  const value = toNumber(item.price, 0) * toNumber(item.quantity, 1);
  const items = [item];

  const ga4Sent = sendGA4Event('add_to_cart', {
    currency: CURRENCY,
    value,
    items
  });
  trackMetaFromGa4('add_to_cart', { items, value });
  return ga4Sent;
};

export const trackAddToCart = (product, quantity = 1) => safelyTrack(
  'trackAddToCart',
  () => trackAddToCartUnsafe(product, quantity)
);

// GA4 only — Meta list events are explicit (trackViewCategory / trackSearchResults)
// so search pages never pollute category audiences.
const trackViewItemListUnsafe = (products, list = {}) => {
  const sourceProducts = Array.isArray(products) ? products.slice(0, 25) : [];
  const items = toGA4Items(sourceProducts);

  if (items.length === 0) {
    return false;
  }

  return sendGA4Event('view_item_list', {
    ...getListParams(list),
    items
  });
};

export const trackViewItemList = (products, list) => safelyTrack(
  'trackViewItemList',
  () => trackViewItemListUnsafe(products, list)
);

/** Meta ViewCategory (custom event — must use trackCustom, not track). */
const trackViewCategoryUnsafe = (products, list = {}) => {
  const sourceProducts = Array.isArray(products) ? products.slice(0, 25) : [];
  const items = toGA4Items(sourceProducts);
  if (items.length === 0) {
    return false;
  }
  const listParams = getListParams(list);
  const metaPayload = toMetaPayloadFromGa4Items(items, calculateGA4ItemsValue(items));
  return trackMetaCustomEvent('ViewCategory', {
    ...metaPayload,
    content_category: String(listParams.item_list_name || listParams.item_list_id || '')
  });
};

export const trackViewCategory = (products, list) => safelyTrack(
  'trackViewCategory',
  () => trackViewCategoryUnsafe(products, list)
);

/**
 * Search results: keep GA4 view_item_list, fire Meta Search with search_string.
 * Use this from SearchResults — not trackViewCategory.
 */
const trackSearchResultsUnsafe = (products, searchString, list = {}) => {
  const sourceProducts = Array.isArray(products) ? products.slice(0, 25) : [];
  const items = toGA4Items(sourceProducts);

  if (items.length === 0) {
    return false;
  }

  const listParams = getListParams(list);
  const ga4Sent = sendGA4Event('view_item_list', {
    ...listParams,
    items
  });

  const value = calculateGA4ItemsValue(items);
  const metaPayload = toMetaPayloadFromGa4Items(items, value);
  trackMetaEvent('Search', {
    ...metaPayload,
    search_string: String(searchString ?? '')
  });

  return ga4Sent;
};

export const trackSearchResults = (products, searchString, list) => safelyTrack(
  'trackSearchResults',
  () => trackSearchResultsUnsafe(products, searchString, list)
);

const trackCompleteRegistrationUnsafe = () => {
  return trackMetaEvent('CompleteRegistration', { status: true });
};

export const trackCompleteRegistration = () => safelyTrack(
  'trackCompleteRegistration',
  () => trackCompleteRegistrationUnsafe()
);

const trackAddToWishlistUnsafe = (product) => {
  const item = toGA4Item(product);
  if (!item.item_id) {
    return false;
  }
  const value = toNumber(item.price, 0) * toNumber(item.quantity, 1);
  const payload = toMetaPayloadFromGa4Items([item], value);
  return trackMetaEvent('AddToWishlist', payload);
};

export const trackAddToWishlist = (product) => safelyTrack(
  'trackAddToWishlist',
  () => trackAddToWishlistUnsafe(product)
);

const trackSelectItemUnsafe = (product, list = {}) => {
  const items = toGA4Items([product]);

  if (items.length === 0) {
    return false;
  }

  // A single-item array would fabricate index 0; report the real list position
  // when the caller provides it, otherwise omit the field entirely.
  if (Number.isInteger(list.index) && list.index >= 0) {
    items[0].index = list.index;
  } else {
    delete items[0].index;
  }

  return sendGA4Event('select_item', {
    ...getListParams(list),
    items
  });
};

export const trackSelectItem = (product, list) => safelyTrack(
  'trackSelectItem',
  () => trackSelectItemUnsafe(product, list)
);

const trackViewCartUnsafe = (cartItems, value) => {
  const items = toGA4Items(cartItems);

  if (items.length === 0) {
    return false;
  }

  return sendGA4Event('view_cart', {
    currency: CURRENCY,
    value: resolveEventValue(value, items),
    items
  });
};

export const trackViewCart = (cartItems, value) => safelyTrack(
  'trackViewCart',
  () => trackViewCartUnsafe(cartItems, value)
);

const trackBeginCheckoutUnsafe = (cartItems, value, coupon) => {
  const items = toGA4Items(cartItems);

  if (items.length === 0) {
    return false;
  }

  const resolvedValue = resolveEventValue(value, items);
  const params = {
    currency: CURRENCY,
    value: resolvedValue,
    items
  };

  if (coupon) {
    params.coupon = coupon;
  }

  const ga4Sent = sendGA4Event('begin_checkout', params);
  trackMetaFromGa4('begin_checkout', { items, value: resolvedValue });
  return ga4Sent;
};

export const trackBeginCheckout = (cartItems, value, coupon) => safelyTrack(
  'trackBeginCheckout',
  () => trackBeginCheckoutUnsafe(cartItems, value, coupon)
);

const trackAddShippingInfoUnsafe = ({ items: cartItems, value, shippingTier } = {}) => {
  const items = toGA4Items(cartItems);

  if (items.length === 0) {
    return false;
  }

  const params = {
    currency: CURRENCY,
    value: resolveEventValue(value, items),
    items
  };

  if (shippingTier) {
    params.shipping_tier = shippingTier;
  }

  return sendGA4Event('add_shipping_info', params);
};

export const trackAddShippingInfo = (params) => safelyTrack(
  'trackAddShippingInfo',
  () => trackAddShippingInfoUnsafe(params)
);

const trackAddPaymentInfoUnsafe = ({ items: cartItems, value, paymentType = 'card' } = {}) => {
  const items = toGA4Items(cartItems);

  if (items.length === 0) {
    return false;
  }

  const resolvedValue = resolveEventValue(value, items);
  const params = {
    currency: CURRENCY,
    value: resolvedValue,
    items
  };

  if (paymentType) {
    params.payment_type = paymentType;
  }

  const ga4Sent = sendGA4Event('add_payment_info', params);
  trackMetaFromGa4('add_payment_info', { items, value: resolvedValue });
  return ga4Sent;
};

export const trackAddPaymentInfo = (params) => safelyTrack(
  'trackAddPaymentInfo',
  () => trackAddPaymentInfoUnsafe(params)
);

// Match keys for Google Ads enhanced conversions. Country is left out on purpose:
// the order only carries a numeric country_id, and resolving it would mean an extra
// request inside the conversion path. Email is the key that actually drives matching.
const toAdsUserData = (order = {}) => {
  const text = (value) => {
    const trimmed = typeof value === 'string' ? value.trim() : '';
    return trimmed || null;
  };

  const email = text(order.email);
  const phoneDigits = (text(order.phonenumber) || '').replace(/[\s().-]/g, '');
  // Google matches on E.164 only, so a local-format number is dropped rather than
  // sent in a shape that can never match.
  const phone = phoneDigits.startsWith('+') ? phoneDigits : null;

  const addressPairs = [
    ['first_name', text(order.fname)],
    ['last_name', text(order.lname)],
    ['street', text(order.address)],
    ['city', text(order.city)],
    ['postal_code', text(order.postcode)]
  ].filter(([, value]) => value);

  const userData = {};
  if (email) {
    userData.email = email.toLowerCase();
  }
  if (phone) {
    userData.phone_number = phone;
  }
  if (addressPairs.length > 0) {
    userData.address = Object.fromEntries(addressPairs);
  }

  return Object.keys(userData).length > 0 ? userData : null;
};

const trackPurchaseUnsafe = (order) => {
  // Prefer order_id for Meta event_id parity with CAPI (purchase_<order_id>).
  const orderPk = order?.order_id ?? order?.id;
  const transactionId = String(order?.order_number || orderPk || '');

  if (!transactionId || !isBrowser()) {
    return false;
  }

  const storageKey = `${PURCHASE_SENT_PREFIX}${transactionId}`;
  if (sentPurchaseIds.has(transactionId) || safeStorageGet(storageKey)) {
    return false;
  }

  const items = toGA4Items(order.items || []);
  if (items.length === 0) {
    // Items not yet available (for example a purchased flag race). Do not latch.
    return false;
  }

  const shipping = toNumber(order.order_shipping ?? order.shipping ?? order.shipping_total, 0);
  const tax = toNumber(order.order_tax ?? order.tax ?? order.tax_total, 0);
  const grossTotal = Number(order.order_total ?? order.final_price ?? order.total ?? order.total_amount);
  // GA4 purchase value excludes tax and shipping. order_total includes shipping in this app.
  const value = Number.isFinite(grossTotal)
    ? Math.max(0, grossTotal - shipping - tax)
    : calculateGA4ItemsValue(items);

  const params = {
    transaction_id: transactionId,
    currency: CURRENCY,
    value,
    tax,
    shipping,
    items
  };

  const couponRecord = order.coupon;
  const couponCode = (
    couponRecord && Number(couponRecord.type) !== 4 && couponRecord.coupon_name
  ) || order.coupon_code || order.couponCode;
  if (couponCode) {
    params.coupon = couponCode;
  }

  const ga4Sent = sendGA4Event('purchase', params);

  // Same event_id as backend metaCapiService so Meta dedupes browser + CAPI.
  const eventID = orderPk != null ? buildPurchaseEventId(orderPk) : buildPurchaseEventId(transactionId);
  const metaSent = trackMetaFromGa4('purchase', { items, value, eventID });

  // Google Ads gets its own conversion hit; transaction_id lets Ads dedupe it.
  const adsSent = sendGoogleAdsPurchaseConversion({
    value,
    currency: CURRENCY,
    transactionId,
    userData: toAdsUserData(order)
  });

  // Latch if any channel fired so a success-page refresh cannot double-count.
  if (ga4Sent || metaSent || adsSent) {
    sentPurchaseIds.add(transactionId);
    safeStorageSet(storageKey, new Date().toISOString());
  }

  return Boolean(ga4Sent || metaSent || adsSent);
};

const waitForAnalyticsInit = async (timeoutMs = 5000, intervalMs = 250) => {
  const start = Date.now();
  while (!ReactGA?.isInitialized && Date.now() - start < timeoutMs) {
    await new Promise((resolve) => setTimeout(resolve, intervalMs));
  }
  return Boolean(ReactGA?.isInitialized);
};

export const trackPurchase = async (order) => {
  try {
    if (isAnalyticsAllowed() && !ReactGA?.isInitialized) {
      await waitForAnalyticsInit();
    }
    return trackPurchaseUnsafe(order);
  } catch (error) {
    console.error('marketingAnalytics trackPurchase failed:', error);
    return false;
  }
};

export const __testing = {
  sentPurchaseIds,
  toGA4Item,
  toGA4Items
};
